/**
 * Calendar-date helpers for HTML date inputs and Firestore Timestamps.
 *
 * Bug this solves: `new Date("YYYY-MM-DD")` is parsed as UTC midnight.
 * In US timezones that becomes the previous local calendar day when formatted.
 */

const toJsDate = (val) => {
  if (val == null || val === "") return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val.toDate === "function") {
    const d = val.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val.seconds === "number") {
    const d = new Date(val.seconds * 1000 + (val.nanoseconds || 0) / 1e6);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    // Date-only string: always treat as local calendar date
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
    }
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** True when the instant is exactly UTC midnight (legacy date-only saves). */
const isUtcMidnight = (d) =>
  d.getUTCHours() === 0 &&
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0;

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Parse an HTML `<input type="date">` value (`YYYY-MM-DD`) into a local Date
 * at noon so timezone conversion never flips the calendar day.
 * @returns {Date|null}
 */
export const parseDateInputLocal = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
};

/**
 * Format a Date / Firestore Timestamp for `<input type="date">` (`YYYY-MM-DD`).
 * Legacy UTC-midnight values use UTC calendar parts so the entered day is preserved.
 */
export const toDateInputValue = (val) => {
  const d = toJsDate(val);
  if (!d) return "";
  if (isUtcMidnight(d)) {
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  }
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Format for UI display as `MM/DD/YYYY` without timezone day-shift.
 */
export const formatCalendarDate = (val, empty = "-") => {
  const d = toJsDate(val);
  if (!d) return empty;
  if (isUtcMidnight(d)) {
    return `${pad2(d.getUTCMonth() + 1)}/${pad2(d.getUTCDate())}/${d.getUTCFullYear()}`;
  }
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
};

/**
 * Comparable timestamp for sorting calendar dates (uses same calendar parts as display).
 * @returns {number|null}
 */
export const calendarDateSortValue = (val) => {
  const d = toJsDate(val);
  if (!d) return null;
  if (isUtcMidnight(d)) {
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};
